import React, { useState, useRef, useEffect } from "react";
import ProgressBar from "../ProgressBar";
import LessonUI from "../ScienceLessonUI";
import { generateAdvancedScienceQuiz } from "../AdvanceScienceQuizPool";
import defaultProfile from "../../UserProfile/EmptyProfile.jpg"
import { ScienceStoryModeQuiz } from "../ScienceStoryModeStructure";
import { awardToken } from "../../awardtoken.js";

export const Grade12ChemistryLessonPages = [
    {
      title: "Introduction to Grade 12 Chemistry",
      content: (
        <>
          <p>
            Welcome to Grade 12 Chemistry! This year, you’ll explore advanced concepts that explain how molecules move, react, and shape the world. Whether you’re heading for university, technology, or research, this course gives you the tools to understand and innovate in our fast-changing society.
          </p>
          <ul>
            <li>Structure & Properties of Matter</li>
            <li>Chemical Kinetics & Equilibrium</li>
            <li>Acids, Bases & Electrochemistry</li>
            <li>Organic Chemistry</li>
          </ul>
          <p>
            <b>Big Idea:</b> Chemistry is not just about reactions in a lab—it’s the science that powers clean energy, new medicines, and sustainable materials for the future!
          </p>
          <p>
            <i>Career Lens:</i> Chemists are innovators in green technology, healthcare, space science, food, and more.
          </p>
        </>
      ),
    },
  
    {
      title: "Chapter 1: Structure & Properties of Matter",
      topic: "Structure & Properties of Matter",   // fixed
      content: (
        <>
          <h4>From Atoms to Advanced Materials</h4>
          <ul>
            <li>How do electron configurations and periodicity explain chemical behavior?</li>
            <li>Comparing intermolecular and intramolecular forces: Why does water boil lower than ethanol?</li>
            <li>Crystal structures, molecular geometry, and their role in everything from salt to silicon chips</li>
            <li>Properties of solids, liquids, and gases—explained by particle theory</li>
          </ul>
          <p>
            <b>Try this:</b> Use a simulation to build crystals or compare boiling points of substances. What trends do you see?
          </p>
          <p>
            <i>Real-World Impact:</i> How are chemists designing new materials for electronics, energy, and medicine at the atomic level?
          </p>
        </>
      ),
    },
  
    {
      title: "Chapter 2: Chemical Kinetics & Equilibrium",
      topic: "Chemical Kinetics & Equilibrium",   // fixed
      content: (
        <>
          <h4>How Fast, How Far?</h4>
          <ul>
            <li>Collision theory, activation energy, and why some reactions “snap” while others are slow</li>
            <li>Factors affecting rates: concentration, temperature, catalysts, and surface area (plus real-world industrial examples)</li>
            <li>Rate laws: interpreting and creating them from lab data</li>
            <li>Dynamic equilibrium and Le Châtelier's Principle: balancing forward and reverse reactions</li>
            <li>Equilibrium constants (K<sub>c</sub>, K<sub>p</sub>): predicting product and reactant concentrations</li>
          </ul>
          <p>
            <b>Investigation:</b> Explore how changing temperature or concentration affects reaction rates with the classic “iodine clock” or online simulations.
          </p>
          <p>
            <i>Futurist View:</i> How are catalysts and equilibrium used in green industry and environmental cleanup (e.g., catalytic converters, ammonia production)?
          </p>
        </>
      ),
    },
  
    {
      title: "Chapter 3: Acids, Bases & Electrochemistry",
      topic: "Acids, Bases & Electrochemistry",    // fixed
      content: (
        <>
          <h4>Chemistry with Charge</h4>
          <ul>
            <li>Acids and bases: Arrhenius, Brønsted-Lowry, and Lewis definitions, plus real-world examples</li>
            <li>pH, pOH, and buffer systems—why life depends on balance</li>
            <li>Titrations and calculations for strong/weak acids and bases</li>
            <li>Redox reactions: electron transfers that power batteries, fuel cells, and photosynthesis</li>
            <li>Electrochemical cells: galvanic/voltaic, electrolytic, and applications in daily life (batteries, corrosion, plating)</li>
          </ul>
          <p>
            <b>Experiment:</b> Try a simple redox or acid-base titration at home or virtually. What is a “buffer,” and how do antacids work?
          </p>
          <p>
            <i>Modern Tech:</i> How is electrochemistry crucial for electric cars, clean energy, and water purification?
          </p>
        </>
      ),
    },
  
    {
      title: "Chapter 4: Organic Chemistry",
      topic: "Organic Chemistry",     // fixed
      content: (
        <>
          <h4>Carbon: The Building Block of Life & Industry</h4>
          <ul>
            <li>Hydrocarbons: alkanes, alkenes, alkynes, and aromatics—plus isomerism</li>
            <li>Key functional groups (alcohols, carboxylic acids, esters, amines, etc.) and their reactions</li>
            <li>Polymerization: from plastics to proteins, how big molecules are built</li>
            <li>Naming and drawing organic molecules (IUPAC), with a focus on real-life molecules (drugs, fuels, plastics)</li>
            <li>Green chemistry: how scientists are inventing safer, sustainable chemicals and bioplastics</li>
          </ul>
          <p>
            <b>Project:</b> Investigate an everyday polymer or pharmaceutical. How was it invented? What is its impact on society or the environment?
          </p>
          <p>
            <i>Vision:</i> What new materials or medicines could chemists invent in the next 20 years?
          </p>
        </>
      ),
    },
  
    {
      title: "Final Review & Looking Ahead",
      isFinalReview: true,
      content: (
        <>
          <p>
            Grade 12 Chemistry empowers you to analyze, invent, and contribute to the most important breakthroughs of the 21st century—from green energy and new materials to medical advances and sustainability.
          </p>
          <ul>
            <li>Review all chapters, quizzes, and key concepts</li>
            <li>Explore recent chemistry breakthroughs (nanotechnology, catalysis, pharmaceuticals, clean energy)</li>
            <li>Reflect: What’s the most important chemistry innovation you’ve learned about? How can you apply this knowledge to your future?</li>
          </ul>
          <p>
            <b>Next steps:</b> Keep exploring, experimenting, and asking questions—chemistry will shape your world!
          </p>
        </>
      ),
    },
  ];
  

export default function Grade12ChemistryLesson({ onAwardTokens }) {
    const [current, setCurrent] = useState(0);
    const [showQuiz, setShowQuiz] = useState(null); // "chapter" or "final"
    const [quizResult, setQuizResult] = useState(null);
    const [user, setUser] = useState(null);
    const hasAwardedToken = useRef(false);
  
    // Track chapters passed
    const [chaptersPassed, setChaptersPassed] = useState(() =>
      JSON.parse(localStorage.getItem("chem12ChaptersPassed") || "{}")
    );
  
    useEffect(() => {
      const logged = JSON.parse(localStorage.getItem("loggedInUser"));
      if (logged) setUser(logged);
    }, []);
  
    // Quiz question logic
    const currentTopic = Grade12ChemistryLessonPages[current]?.topic;
    const isFinalReview = Grade12ChemistryLessonPages[current]?.isFinalReview;
    let quizQuestions = [];
    if (showQuiz === "chapter" && currentTopic) {
      quizQuestions = generateAdvancedScienceQuiz({
        subject: "Chemistry",
        grade: 12,
        topic: currentTopic,
        count: 5,
      });
    } else if (showQuiz === "final") {
      const topics = [
        "Structure & Properties of Matter",
        "Chemical Kinetics",
        "Equilibrium & Acids-Bases",
        "Electrochemistry",
        "Organic Chemistry"
      ];
      let pool = [];
      topics.forEach(topic => {
        pool = pool.concat(generateAdvancedScienceQuiz({
          subject: "Chemistry",
          grade: 12,
          topic,
          count: 10,
        }));
      });
      quizQuestions = pool.sort(() => Math.random() - 0.5).slice(0, 15);
    }
  
    useEffect(() => {
      if (quizResult && !hasAwardedToken.current) {
        const percent = (quizResult.score / quizResult.total) * 100;
        if (percent >= 80) {
          if (typeof onAwardTokens === "function") {
            onAwardTokens(1);
          } else {
            awardToken();
          }
          hasAwardedToken.current = true;
        }
      }
    }, [quizResult, onAwardTokens]);
  
    // Navigation: gated just like Bio 12
    const handleNextPage = () => {
      const nextIndex = current + 1;
      const nextPage = Grade12ChemistryLessonPages[nextIndex];
  
      // Only gate if currentTopic is set (i.e., not intro page)
      if (
        nextPage &&
        currentTopic &&
        nextPage.topic !== currentTopic &&
        !chaptersPassed[currentTopic]
      ) {
        setShowQuiz("chapter");
        setQuizResult(null);
        return;
      }
  
      setCurrent(nextIndex);
    };
  
    // Quiz UI (marks chapter as passed if successful)
    if (showQuiz && quizQuestions.length > 0) {
      return (
        <ScienceStoryModeQuiz
          questions={quizQuestions}
          onFinish={(score, total) => {
  setShowQuiz(null);
  setQuizResult({ score, total });
  hasAwardedToken.current = false;
  const percent = total > 0 ? Math.round((score / total) * 100) : 0;

  // --- Save quiz result for Student Dashboard ---
  if (showQuiz === "chapter" && currentTopic) {
    const quizResults = JSON.parse(localStorage.getItem("quizResults") || "{}");
    quizResults[`chemistry_g12_${currentTopic}`] = {
      score,
      total,
      percent,
      lastCompleted: Date.now(),
    };
    localStorage.setItem("quizResults", JSON.stringify(quizResults));
  }
  if (showQuiz === "final") {
    const quizResults = JSON.parse(localStorage.getItem("quizResults") || "{}");
    quizResults["chemistry_g12_final"] = {
      score,
      total,
      percent,
      lastCompleted: Date.now(),
    };
    localStorage.setItem("quizResults", JSON.stringify(quizResults));
  }
  // ---------------------------------------------

  if (percent >= 80 && currentTopic && showQuiz === "chapter") {
    setChaptersPassed(prev => {
      const updated = { ...prev, [currentTopic]: true };
      localStorage.setItem("chem12ChaptersPassed", JSON.stringify(updated));
      return updated;
    });
  }
}}
          studentAvatar={user?.avatar ?? defaultProfile}
        />
      );
    }
  
    // Quiz result/congrats screen
    if (quizResult) {
      const percent = Math.round((quizResult.score / quizResult.total) * 100);
      const passed = percent >= 80;
      return (
        <div className="quiz-result-container" style={{ textAlign: "center", marginTop: 48 }}>
          <h2 style={{ color: "#d4145a" }}>Quiz Complete!</h2>
          <p style={{ fontSize: 22, margin: "18px 0" }}>
            Your Score: {quizResult.score} / {quizResult.total} ({percent}%)
          </p>
          {passed ? (
            <div style={{ color: "#228B22", fontSize: 24, margin: "16px 0" }}>
              🎉 Congratulations! You earned a Science token!
            </div>
          ) : (
            <div style={{ color: "#e67e22", fontSize: 20, margin: "16px 0" }}>
              Try again to earn a Science token (80% required).
            </div>
          )}
          {passed ? (
            <button
              className="button1"
              style={{ marginTop: 28 }}
              onClick={() => {
                setQuizResult(null);
                setCurrent(c => c + 1);
              }}
            >
              Next Chapter
            </button>
          ) : (
            <button
              className="button1"
              style={{ marginTop: 28 }}
              onClick={() => setQuizResult(null)}
            >
              Return to Lesson
            </button>
          )}
        </div>
      );
    }
  
    // Main lesson rendering
    return (
      <div className="lesson-page">
        <ProgressBar progress={Math.round((current / (Grade12ChemistryLessonPages.length - 1)) * 100)} />
        <LessonUI
          pages={[Grade12ChemistryLessonPages[current]]}
          currentPageIndex={0}
          onPrevPage={current > 0 ? () => setCurrent(c => c - 1) : undefined}
          onNextPage={current < Grade12ChemistryLessonPages.length - 1 ? handleNextPage : undefined}
          backTo="/science"
        />
        {/* Chapter quiz (for slides with a topic) */}
        {currentTopic && !isFinalReview && (
          <div style={{ textAlign: "center", marginTop: 24 }}>
            <button className="button1" onClick={() => {
              setShowQuiz("chapter");
              setQuizResult(null);
              hasAwardedToken.current = false;
            }}>
              Start Quiz for This Chapter
            </button>
          </div>
        )}
        {/* Final Quiz button (only on last slide) */}
        {isFinalReview && (
          <div style={{ textAlign: "center", marginTop: 24 }}>
            <button className="button1" onClick={() => {
              setShowQuiz("final");
              setQuizResult(null);
              hasAwardedToken.current = false;
            }}>
              Take Final Quiz for Grade 12 Chemistry
            </button>
          </div>
        )}
      </div>
    );
  }
  
  